import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { api } from "../api";

import "./ScreenplayScriptWorkspace.css";

const ELEMENT_TYPES = [
  "scene_heading",
  "action",
  "character",
  "dialogue",
  "parenthetical",
  "transition",
];

const TYPE_LABELS = {
  scene_heading: {
    en: "Scene Heading",
    fr: "En-tête de scène",
  },
  action: {
    en: "Action",
    fr: "Action",
  },
  character: {
    en: "Character Cue",
    fr: "Personnage",
  },
  dialogue: {
    en: "Dialogue",
    fr: "Dialogue",
  },
  parenthetical: {
    en: "Parenthetical",
    fr: "Parenthèse",
  },
  transition: {
    en: "Transition",
    fr: "Transition",
  },
};

const PLACEHOLDERS = {
  scene_heading: {
    en: "Enter scene heading",
    fr: "Saisissez l’en-tête de scène",
  },
  action: {
    en: "Describe what we see and hear.",
    fr: "Décrivez ce que nous voyons et entendons.",
  },
  character: {
    en: "CHARACTER NAME",
    fr: "NOM DU PERSONNAGE",
  },
  dialogue: {
    en: "Write the line of dialogue.",
    fr: "Écrivez la réplique.",
  },
  parenthetical: {
    en: "(quietly)",
    fr: "(doucement)",
  },
  transition: {
    en: "CUT TO:",
    fr: "COUPE À :",
  },
};

function clean(value) {
  return value == null
    ? ""
    : String(value).trim();
}

function newId(prefix) {
  return [
    prefix,
    Date.now().toString(36),
    Math.random()
      .toString(36)
      .slice(2, 10),
  ].join("-");
}

function normalizedScenes(value) {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map(
    (scene) => ({
      sceneId:
        clean(scene?.sceneId),
      elements:
        Array.isArray(
          scene?.elements
        )
          ? scene.elements.map(
              (element) => ({
                elementId:
                  clean(
                    element
                      ?.elementId
                  ),
                type:
                  ELEMENT_TYPES
                    .includes(
                      clean(
                        element?.type
                      ).toLowerCase()
                    )
                    ? clean(
                        element?.type
                      ).toLowerCase()
                    : "action",
                text:
                  element?.text ==
                  null
                    ? ""
                    : String(
                        element.text
                      ),
              })
            )
          : [],
    })
  );
}

function signature(scenes) {
  return JSON.stringify(
    normalizedScenes(scenes)
  );
}

function moveItem(
  items,
  fromIndex,
  toIndex
) {
  if (
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= items.length ||
    toIndex >= items.length ||
    fromIndex === toIndex
  ) {
    return items;
  }

  const next = [...items];
  const [item] =
    next.splice(
      fromIndex,
      1
    );

  next.splice(
    toIndex,
    0,
    item
  );

  return next;
}

function resizeScreenplayTextarea(
  node
) {
  if (!node) {
    return;
  }

  node.style.height =
    "auto";

  node.style.height =
    `${Math.max(
      38,
      node.scrollHeight || 0
    )}px`;
}

function screenplayElementClass(
  type
) {
  return `ns-screenplay-element-${clean(
    type
  ).replace(/_/g, "-")}`;
}

function sceneHeadingText(scene) {
  const heading =
    (
      Array.isArray(
        scene?.elements
      )
        ? scene.elements
        : []
    ).find(
      (element) =>
        clean(
          element?.type
        ) ===
        "scene_heading"
    );

  return clean(
    heading?.text
  );
}


function sceneFinderText(
  scene,
  sceneIndex
) {
  const number =
    sceneIndex + 1;

  const elements =
    Array.isArray(
      scene?.elements
    )
      ? scene.elements
      : [];

  return [
    String(number),
    `scene ${number}`,
    `scène ${number}`,
    sceneHeadingText(scene),
    ...elements.map(
      (element) =>
        clean(element?.text)
    ),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function sceneFinderSnippet(scene) {
  const elements =
    Array.isArray(
      scene?.elements
    )
      ? scene.elements
      : [];

  const body =
    elements.find(
      (element) =>
        clean(element?.type) !==
          "scene_heading" &&
        clean(element?.text)
    );

  return clean(
    body?.text
  )
    .replace(/\s+/g, " ")
    .slice(0, 150);
}

function sceneFinderScore(
  scene,
  sceneIndex,
  query
) {
  const normalized =
    clean(query)
      .toLowerCase();

  if (!normalized) {
    return 0;
  }

  const number =
    sceneIndex + 1;

  if (
    normalized === String(number) ||
    normalized === `scene ${number}` ||
    normalized === `scène ${number}`
  ) {
    return 500;
  }

  const heading =
    sceneHeadingText(scene)
      .toLowerCase();

  if (heading === normalized) {
    return 400;
  }

  if (
    heading &&
    heading.startsWith(normalized)
  ) {
    return 300;
  }

  if (
    heading &&
    heading.includes(normalized)
  ) {
    return 250;
  }

  return sceneFinderText(
    scene,
    sceneIndex
  ).includes(normalized)
    ? 100
    : 0;
}


export default function ScreenplayScriptWorkspace({
  projectId,
  active = false,
  isFR = false,
}) {
  const language =
    isFR ? "fr" : "en";

  const id =
    clean(projectId);

  const [
    scenes,
    setScenes,
  ] = useState([]);

  const [
    baselineScenes,
    setBaselineScenes,
  ] = useState([]);

  const [
    revision,
    setRevision,
  ] = useState(0);

  const [
    activeSceneId,
    setActiveSceneId,
  ] = useState("");

  const [
    editingSceneId,
    setEditingSceneId,
  ] = useState("");

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    loadError,
    setLoadError,
  ] = useState("");

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    message,
    setMessage,
  ] = useState("");

  const [
    approvedDraft,
    setApprovedDraft,
  ] = useState(null);

  const [
    releasedDraft,
    setReleasedDraft,
  ] = useState(null);

  const [
    approving,
    setApproving,
  ] = useState(false);

  const [
    approvalDialogRevision,
    setApprovalDialogRevision,
  ] = useState(null);

  const [
    releasing,
    setReleasing,
  ] = useState(false);

  const [
    releaseDialogApprovedDraft,
    setReleaseDialogApprovedDraft,
  ] = useState(null);

  const [
    sceneFinderOpen,
    setSceneFinderOpen,
  ] = useState(false);

  const [
    sceneFinderQuery,
    setSceneFinderQuery,
  ] = useState("");

  const loadedProjectIdRef =
    useRef("");

  const loadRequestRef =
    useRef(0);

  const currentProjectIdRef =
    useRef(id);

  const workspaceRootRef =
    useRef(null);

  const sceneStripRef =
    useRef(null);

  const currentScenesRef =
    useRef([]);

  currentProjectIdRef.current =
    id;

  currentScenesRef.current =
    scenes;

  const dirty =
    useMemo(
      () =>
        signature(scenes) !==
        signature(
          baselineScenes
        ),
      [
        scenes,
        baselineScenes,
      ]
    );

  const activeSceneIndex =
    useMemo(
      () => {
        if (
          scenes.length === 0
        ) {
          return -1;
        }

        const index =
          scenes.findIndex(
            (scene) =>
              clean(
                scene?.sceneId
              ) ===
              clean(
                activeSceneId
              )
          );

        return index >= 0
          ? index
          : 0;
      },
      [
        activeSceneId,
        scenes,
      ]
    );

  const activeScene =
    activeSceneIndex >= 0
      ? scenes[
          activeSceneIndex
        ]
      : null;

  const sceneFinderResults =
    useMemo(
      () => {
        const query =
          clean(
            sceneFinderQuery
          );

        if (!query) {
          return [];
        }

        return scenes
          .map(
            (
              scene,
              sceneIndex
            ) => ({
              scene,
              sceneIndex,
              score:
                sceneFinderScore(
                  scene,
                  sceneIndex,
                  query
                ),
            })
          )
          .filter(
            (result) =>
              result.score > 0
          )
          .sort(
            (left, right) =>
              right.score -
                left.score ||
              left.sceneIndex -
                right.sceneIndex
          );
      },
      [
        sceneFinderQuery,
        scenes,
      ]
    );

  const visibleSceneFinderResults =
    sceneFinderResults.slice(
      0,
      20
    );


  const isEditingActiveScene =
    Boolean(
      activeScene &&
      clean(
        editingSceneId
      ) ===
        clean(
          activeScene
            ?.sceneId
        )
    );

  const textareaLayoutKey =
    useMemo(
      () =>
        scenes
          .map(
            (scene) =>
              [
                scene.sceneId,
                ...scene.elements.map(
                  (element) =>
                    `${element.elementId}:${element.type}`
                ),
              ].join("|")
          )
          .join("||"),
      [scenes]
    );

  const loadDraft =
    useCallback(
      async (
        targetProjectId
      ) => {
    const target =
      clean(targetProjectId);

    if (!target) {
      return;
    }

    const requestId =
      loadRequestRef.current +
      1;

    loadRequestRef.current =
      requestId;

    setLoading(true);
    setLoadError("");
    setMessage("");

    try {
      const [
        payload,
        approvalPayload,
        releasePayload,
      ] =
        await Promise.all([
          api
            .getScreenplayScriptDraft(
              target
            ),
          api
            .getScreenplayApprovedDraft(
              target
            ),
          api
            .getScreenplayReleasedDraft(
              target
            ),
        ]);

      if (
        requestId !==
          loadRequestRef
            .current ||
        currentProjectIdRef
          .current !== target
      ) {
        return;
      }

      const draft =
        payload?.scriptDraft ||
        {};

      setApprovedDraft(
        approvalPayload
          ?.approvedDraft ||
        null
      );

      setReleasedDraft(
        releasePayload
          ?.releasedDraft ||
        null
      );

      const nextScenes =
        normalizedScenes(
          draft.scenes
        );

      setScenes(
        nextScenes
      );

      setBaselineScenes(
        nextScenes
      );

      setActiveSceneId(
        clean(
          nextScenes[0]
            ?.sceneId
        )
      );

      setEditingSceneId("");

      setRevision(
        Number.isInteger(
          Number(
            draft.revision
          )
        )
          ? Number(
              draft.revision
            )
          : 0
      );

      loadedProjectIdRef
        .current =
        target;
    } catch (error) {
      if (
        requestId !==
          loadRequestRef
            .current ||
        currentProjectIdRef
          .current !== target
      ) {
        return;
      }

      setLoadError(
        clean(
          error?.message
        ) ||
          (
            isFR
              ? "Impossible d’ouvrir le brouillon du scénario."
              : "Could not open the Screenplay draft."
          )
      );
    } finally {
      if (
        requestId ===
          loadRequestRef
            .current &&
        currentProjectIdRef
          .current === target
      ) {
        setLoading(false);
      }
    }
      },
      [isFR]
    );

  useEffect(
    () => {
      if (
        !active ||
        !id
      ) {
        return;
      }

      if (
        loadedProjectIdRef
          .current === id
      ) {
        return;
      }

      setScenes([]);
      setBaselineScenes([]);
      setRevision(0);
      setActiveSceneId("");
      setEditingSceneId("");
      setLoadError("");
      setMessage("");
      setSaving(false);
      setApprovedDraft(null);
      setReleasedDraft(null);
      setApproving(false);
      setApprovalDialogRevision(
        null
      );
      setReleasing(false);
      setReleaseDialogApprovedDraft(
        null
      );
      setSceneFinderOpen(false);
      setSceneFinderQuery("");

      loadDraft(id);
    },
    [
      active,
      id,
      loadDraft,
    ]
  );

  useEffect(
    () => {
      if (
        scenes.length === 0
      ) {
        if (activeSceneId) {
          setActiveSceneId("");
        }

        if (editingSceneId) {
          setEditingSceneId("");
        }

        return;
      }

      const activeStillExists =
        scenes.some(
          (scene) =>
            clean(
              scene?.sceneId
            ) ===
            clean(
              activeSceneId
            )
        );

      if (!activeStillExists) {
        setActiveSceneId(
          clean(
            scenes[0]
              ?.sceneId
          )
        );
        setEditingSceneId("");
      }
    },
    [
      activeSceneId,
      editingSceneId,
      scenes,
    ]
  );

  useEffect(
    () => {
      const strip =
        sceneStripRef.current;

      if (
        !strip ||
        activeSceneIndex < 0
      ) {
        return;
      }

      const card =
        strip.querySelector(
          `[data-screenplay-scene-index="${activeSceneIndex}"]`
        );

      if (!card) {
        return;
      }

      const maxLeft =
        Math.max(
          0,
          strip.scrollWidth -
            strip.clientWidth
        );

      const centeredLeft =
        card.offsetLeft -
        (
          strip.clientWidth -
          card.offsetWidth
        ) /
          2;

      const nextLeft =
        Math.min(
          maxLeft,
          Math.max(
            0,
            centeredLeft
          )
        );

      if (
        typeof strip.scrollTo ===
        "function"
      ) {
        strip.scrollTo({
          left: nextLeft,
          behavior: "smooth",
        });
      } else {
        strip.scrollLeft =
          nextLeft;
      }
    },
    [
      activeSceneIndex,
      scenes.length,
    ]
  );

  useEffect(
    () => {
      const root =
        workspaceRootRef.current;

      if (!root) {
        return;
      }

      root
        .querySelectorAll(
          'textarea[data-screenplay-element-text="true"]'
        )
        .forEach(
          resizeScreenplayTextarea
        );
    },
    [textareaLayoutKey]
  );

  function selectScene(
    sceneIndex
  ) {
    if (
      sceneIndex < 0 ||
      sceneIndex >=
        scenes.length
    ) {
      return;
    }

    if (dirty) {
      setMessage(
        isFR
          ? "Enregistrez les modifications de la scène active avant de changer de scène."
          : "Save the active Scene before moving to another Scene."
      );
      return;
    }

    setActiveSceneId(
      clean(
        scenes[
          sceneIndex
        ]?.sceneId
      )
    );
    setEditingSceneId("");
    setMessage("");
  }

  function toggleSceneFinder() {
    setSceneFinderOpen(
      (current) =>
        !current
    );

    if (sceneFinderOpen) {
      setSceneFinderQuery("");
    }

    setMessage("");
  }

  function closeSceneFinder() {
    setSceneFinderOpen(false);
    setSceneFinderQuery("");
  }

  function chooseSceneFinderResult(
    sceneIndex
  ) {
    if (
      sceneIndex ===
        activeSceneIndex
    ) {
      closeSceneFinder();
      setMessage("");
      return;
    }

    if (dirty) {
      selectScene(sceneIndex);
      return;
    }

    selectScene(sceneIndex);
    closeSceneFinder();
  }

  function beginSceneEdit() {
    if (!activeScene) {
      return;
    }

    setEditingSceneId(
      clean(
        activeScene.sceneId
      )
    );
    setMessage("");
  }

  function addScene() {
    if (dirty) {
      setMessage(
        isFR
          ? "Enregistrez les modifications de la scène active avant d’ajouter une autre scène."
          : "Save the active Scene before adding another Scene."
      );
      return;
    }

    const sceneId =
      newId("scene");

    const elementId =
      newId("element");

    setActiveSceneId(
      sceneId
    );
    setEditingSceneId(
      sceneId
    );

    setScenes(
      (current) => [
        ...current,
        {
          sceneId,
          elements: [
            {
              elementId,
              type:
                "scene_heading",
              text: "",
            },
          ],
        },
      ]
    );

    setMessage("");
  }

  function removeScene(
    sceneIndex
  ) {
    setScenes(
      (current) => {
        const next =
          current.filter(
            (_, index) =>
              index !==
              sceneIndex
          );

        const nextIndex =
          Math.min(
            sceneIndex,
            Math.max(
              0,
              next.length - 1
            )
          );

        setActiveSceneId(
          clean(
            next[
              nextIndex
            ]?.sceneId
          )
        );

        setEditingSceneId(
          clean(
            next[
              nextIndex
            ]?.sceneId
          )
        );

        return next;
      }
    );

    setMessage("");
  }

  function moveScene(
    sceneIndex,
    direction
  ) {
    setScenes(
      (current) =>
        moveItem(
          current,
          sceneIndex,
          sceneIndex +
            direction
        )
    );

    setMessage("");
  }

  function addElement(
    sceneIndex
  ) {
    setScenes(
      (current) =>
        current.map(
          (
            scene,
            index
          ) =>
            index ===
            sceneIndex
              ? {
                  ...scene,
                  elements: [
                    ...scene
                      .elements,
                    {
                      elementId:
                        newId(
                          "element"
                        ),
                      type:
                        "action",
                      text: "",
                    },
                  ],
                }
              : scene
        )
    );

    setMessage("");
  }

  function updateElement(
    sceneIndex,
    elementIndex,
    field,
    value
  ) {
    setScenes(
      (current) =>
        current.map(
          (
            scene,
            index
          ) =>
            index ===
            sceneIndex
              ? {
                  ...scene,
                  elements:
                    scene
                      .elements
                      .map(
                        (
                          element,
                          innerIndex
                        ) =>
                          innerIndex ===
                          elementIndex
                            ? {
                                ...element,
                                [field]:
                                  value,
                              }
                            : element
                      ),
                }
              : scene
        )
    );

    setMessage("");
  }

  function removeElement(
    sceneIndex,
    elementIndex
  ) {
    setScenes(
      (current) =>
        current.map(
          (
            scene,
            index
          ) =>
            index ===
            sceneIndex
              ? {
                  ...scene,
                  elements:
                    scene
                      .elements
                      .filter(
                        (
                          _,
                          innerIndex
                        ) =>
                          innerIndex !==
                          elementIndex
                      ),
                }
              : scene
        )
    );

    setMessage("");
  }

  function moveElement(
    sceneIndex,
    elementIndex,
    direction
  ) {
    setScenes(
      (current) =>
        current.map(
          (
            scene,
            index
          ) =>
            index ===
            sceneIndex
              ? {
                  ...scene,
                  elements:
                    moveItem(
                      scene
                        .elements,
                      elementIndex,
                      elementIndex +
                        direction
                    ),
                }
              : scene
        )
    );

    setMessage("");
  }

  async function saveDraft() {
    if (
      !id ||
      saving ||
      !dirty
    ) {
      return;
    }

    const expectedRevision =
      Number(revision);

    if (
      !Number.isInteger(
        expectedRevision
      ) ||
      expectedRevision < 0
    ) {
      setMessage(
        isFR
          ? "La révision du brouillon n’est pas valide. Rechargez le projet."
          : "The draft revision is invalid. Reload the project."
      );
      return;
    }

    const submittedScenes =
      normalizedScenes(
        scenes
      );

    const submittedSignature =
      signature(
        submittedScenes
      );

    setSaving(true);
    setMessage("");

    try {
      const payload =
        await api
          .saveScreenplayScriptDraft(
            id,
            {
              scriptDraft: {
                scenes:
                  submittedScenes,
              },
              expectedRevision,
            }
          );

      if (
        currentProjectIdRef
          .current !== id
      ) {
        return;
      }

      const draft =
        payload?.scriptDraft;

      if (!draft) {
        throw new Error(
          isFR
            ? "Le serveur n’a pas retourné le brouillon enregistré."
            : "The server did not return the saved Screenplay draft."
        );
      }

      const nextScenes =
        normalizedScenes(
          draft.scenes
        );

      const submittedStillCurrent =
        signature(
          currentScenesRef
            .current
        ) ===
        submittedSignature;

      setScenes(
        (current) =>
          signature(
            current
          ) ===
          submittedSignature
            ? nextScenes
            : current
      );

      setBaselineScenes(
        nextScenes
      );

      setRevision(
        Number(
          draft.revision ||
          0
        )
      );

      loadedProjectIdRef
        .current =
        id;

      if (
        submittedStillCurrent
      ) {
        setEditingSceneId("");
      }

      setMessage(
        submittedStillCurrent
          ? (
              isFR
                ? "Scène enregistrée."
                : "Scene saved."
            )
          : (
              isFR
                ? "La scène a été enregistrée, mais des modifications plus récentes restent à enregistrer."
                : "The Scene was saved, but newer edits still need to be saved."
            )
      );
    } catch (error) {
      if (
        currentProjectIdRef
          .current !== id
      ) {
        return;
      }

      setMessage(
        clean(
          error?.message
        ) ||
          (
            isFR
              ? "Impossible d’enregistrer le brouillon. Vos modifications restent ici."
              : "Could not save the draft. Your changes remain here."
          )
      );
    } finally {
      if (
        currentProjectIdRef
          .current === id
      ) {
        setSaving(false);
      }
    }
  }

  function requestApproveDraft() {
    if (
      !id ||
      approving ||
      saving
    ) {
      return;
    }

    if (dirty) {
      setMessage(
        isFR
          ? "Enregistrez vos modifications avant d’approuver ce brouillon."
          : "Save your changes before approving this draft."
      );
      return;
    }

    const currentRevision =
      Number(revision);

    if (
      !Number.isInteger(
        currentRevision
      ) ||
      currentRevision < 1
    ) {
      setMessage(
        isFR
          ? "Enregistrez le scénario avant d’approuver un brouillon."
          : "Save the screenplay before approving a draft."
      );
      return;
    }

    setMessage("");
    setApprovalDialogRevision(
      currentRevision
    );
  }

  function cancelApproveDraft() {
    if (approving) {
      return;
    }

    setApprovalDialogRevision(
      null
    );
  }

  async function confirmApproveDraft() {
    if (
      !id ||
      approving ||
      saving ||
      approvalDialogRevision ===
        null
    ) {
      return;
    }

    const targetRevision =
      Number(
        approvalDialogRevision
      );

    if (
      !Number.isInteger(
        targetRevision
      ) ||
      targetRevision < 1 ||
      dirty ||
      Number(revision) !==
        targetRevision
    ) {
      setApprovalDialogRevision(
        null
      );

      setMessage(
        isFR
          ? "Le brouillon a changé depuis l’ouverture de la confirmation. Enregistrez-le puis réessayez."
          : "The draft changed after this confirmation was opened. Save it and try again."
      );
      return;
    }

    setApproving(true);
    setMessage("");

    try {
      const payload =
        await api
          .approveScreenplayDraft(
            id,
            {
              expectedDraftRevision:
                targetRevision,
            }
          );

      if (
        currentProjectIdRef
          .current !== id
      ) {
        return;
      }

      const nextApproved =
        payload?.approvedDraft ||
        null;

      if (!nextApproved) {
        throw new Error(
          isFR
            ? "Le serveur n’a pas retourné le brouillon approuvé."
            : "The server did not return the approved Screenplay draft."
        );
      }

      setApprovedDraft(
        nextApproved
      );

      setApprovalDialogRevision(
        null
      );

      setMessage(
        isFR
          ? "Brouillon approuvé et conservé. Vous pouvez continuer à écrire ; les modifications futures ne changeront pas cette version approuvée."
          : "Draft approved and preserved. You can keep writing; future edits will not change this approved version."
      );
    } catch (error) {
      if (
        currentProjectIdRef
          .current !== id
      ) {
        return;
      }

      setMessage(
        clean(
          error?.message
        ) ||
          (
            isFR
              ? "Impossible d’approuver le brouillon."
              : "Could not approve the draft."
          )
      );
    } finally {
      if (
        currentProjectIdRef
          .current === id
      ) {
        setApproving(false);
      }
    }
  }


  function requestReleaseApprovedDraft() {
    if (
      !id ||
      releasing ||
      approving ||
      !approvedDraft?.id
    ) {
      return;
    }

    setMessage("");
    setReleaseDialogApprovedDraft({
      id:
        clean(
          approvedDraft.id
        ),
      sourceDraftRevision:
        Number(
          approvedDraft
            .sourceDraftRevision ||
          0
        ),
    });
  }

  function cancelReleaseApprovedDraft() {
    if (releasing) {
      return;
    }

    setReleaseDialogApprovedDraft(
      null
    );
  }

  async function confirmReleaseApprovedDraft() {
    if (
      !id ||
      releasing ||
      !releaseDialogApprovedDraft
        ?.id
    ) {
      return;
    }

    const targetApprovedDraftId =
      clean(
        releaseDialogApprovedDraft
          .id
      );

    const currentApprovedDraftId =
      clean(
        approvedDraft?.id
      );

    if (
      !targetApprovedDraftId ||
      targetApprovedDraftId !==
        currentApprovedDraftId
    ) {
      setReleaseDialogApprovedDraft(
        null
      );

      setMessage(
        isFR
          ? "Le brouillon approuvé a changé depuis l’ouverture de la confirmation. Rechargez le projet puis réessayez."
          : "The Approved Draft changed after this confirmation was opened. Reload the project and try again."
      );
      return;
    }

    setReleasing(true);
    setMessage("");

    try {
      const payload =
        await api
          .releaseScreenplayApprovedDraft(
            id,
            {
              expectedApprovedDraftId:
                targetApprovedDraftId,
            }
          );

      if (
        currentProjectIdRef
          .current !== id
      ) {
        return;
      }

      const nextReleased =
        payload?.releasedDraft ||
        null;

      if (!nextReleased) {
        throw new Error(
          isFR
            ? "Le serveur n’a pas retourné le brouillon diffusé."
            : "The server did not return the Released Draft."
        );
      }

      setReleasedDraft(
        nextReleased
      );

      setReleaseDialogApprovedDraft(
        null
      );

      setMessage(
        isFR
          ? `Le brouillon approuvé · Révision ${nextReleased.sourceDraftRevision} est maintenant le brouillon diffusé. Reader View et la publication publique restent des étapes distinctes.`
          : `Approved Draft · Revision ${nextReleased.sourceDraftRevision} is now the Released Draft. Reader View and public publication remain separate steps.`
      );
    } catch (error) {
      if (
        currentProjectIdRef
          .current !== id
      ) {
        return;
      }

      setMessage(
        clean(
          error?.message
        ) ||
          (
            isFR
              ? "Impossible de diffuser le brouillon approuvé."
              : "Could not release the Approved Draft."
          )
      );
    } finally {
      if (
        currentProjectIdRef
          .current === id
      ) {
        setReleasing(false);
      }
    }
  }


  if (loading) {
    return (
      <div
        className="ns-card"
        style={{
          padding: 22,
        }}
      >
        {isFR
          ? "Ouverture du brouillon du scénario…"
          : "Opening Screenplay draft…"}
      </div>
    );
  }

  if (
    loadError &&
    loadedProjectIdRef
      .current !== id
  ) {
    return (
      <div
        className="ns-card"
        style={{
          padding: 22,
        }}
      >
        <strong>
          {loadError}
        </strong>

        <div
          style={{
            marginTop: 14,
          }}
        >
          <button
            type="button"
            className="ns-button ns-button-outline"
            onClick={() =>
              loadDraft(id)
            }
          >
            {isFR
              ? "Réessayer"
              : "Try again"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={workspaceRootRef}
      className="ns-card ns-screenplay-workspace"
      style={{
        padding: 22,
      }}
    >
      {releaseDialogApprovedDraft ? (
        <div
          className="screenplay-release-dialog-backdrop"
          onMouseDown={(
            event
          ) => {
            if (
              event.target ===
                event.currentTarget &&
              !releasing
            ) {
              cancelReleaseApprovedDraft();
            }
          }}
          style={{
            position:
              "fixed",
            inset: 0,
            zIndex: 10000,
            display: "grid",
            placeItems:
              "center",
            padding: 20,
            background:
              "rgba(15,23,42,0.58)",
          }}
        >
          <section
            className="screenplay-release-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="screenplay-release-dialog-title"
            aria-describedby="screenplay-release-dialog-description screenplay-release-dialog-note"
            style={{
              width:
                "min(100%, 560px)",
              padding: 24,
              border:
                "1px solid #fde68a",
              borderRadius: 18,
              background:
                "#ffffff",
              boxShadow:
                "0 24px 70px rgba(15,23,42,0.28)",
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 900,
                letterSpacing:
                  "0.1em",
                textTransform:
                  "uppercase",
                color:
                  "#92400e",
              }}
            >
              {isFR
                ? "BROUILLON DIFFUSÉ"
                : "RELEASED DRAFT"}
            </div>

            <h3
              id="screenplay-release-dialog-title"
              style={{
                margin:
                  "7px 0 8px",
                color:
                  "#0f172a",
              }}
            >
              {isFR
                ? `Diffuser le brouillon approuvé · Révision ${releaseDialogApprovedDraft.sourceDraftRevision} ?`
                : `Release Approved Draft · Revision ${releaseDialogApprovedDraft.sourceDraftRevision}?`}
            </h3>

            <p
              id="screenplay-release-dialog-description"
              style={{
                margin:
                  "0 0 10px",
                color:
                  "#334155",
                lineHeight: 1.55,
              }}
            >
              {isFR
                ? "Cette version approuvée exacte deviendra l’autorité du brouillon diffusé du scénario. Les versions futures n’écraseront pas cette version."
                : "This exact Approved Draft will become the screenplay’s Released Draft authority. Future Drafts or approvals will not overwrite this release."}
            </p>

            <p
              id="screenplay-release-dialog-note"
              className="ns-card-meta"
              style={{
                margin: 0,
                lineHeight: 1.5,
                fontWeight: 700,
              }}
            >
              {isFR
                ? "Cela ne publie pas le scénario sur le site public et ne génère aucun fichier."
                : "This does not publish the screenplay to the public site or generate any files."}
            </p>

            <div
              style={{
                display:
                  "flex",
                flexWrap:
                  "wrap",
                justifyContent:
                  "flex-end",
                gap: 10,
                marginTop: 22,
              }}
            >
              <button
                type="button"
                autoFocus
                className="ns-button ns-button-outline"
                disabled={
                  releasing
                }
                onClick={
                  cancelReleaseApprovedDraft
                }
              >
                {isFR
                  ? "Annuler"
                  : "Cancel"}
              </button>

              <button
                type="button"
                className="ns-button"
                disabled={
                  releasing
                }
                onClick={
                  confirmReleaseApprovedDraft
                }
                style={{
                  background:
                    "#b45309",
                  borderColor:
                    "#b45309",
                  color:
                    "#ffffff",
                }}
              >
                {releasing
                  ? (
                      isFR
                        ? "Diffusion…"
                        : "Releasing…"
                    )
                  : (
                      isFR
                        ? "Diffuser le brouillon"
                        : "Release draft"
                    )}
              </button>
            </div>
          </section>
        </div>
      ) : null}

      {approvalDialogRevision !==
      null ? (
        <div
          className="screenplay-approval-dialog-backdrop"
          onMouseDown={(
            event
          ) => {
            if (
              event.target ===
                event.currentTarget &&
              !approving
            ) {
              cancelApproveDraft();
            }
          }}
          style={{
            position:
              "fixed",
            inset: 0,
            zIndex: 10000,
            display: "grid",
            placeItems:
              "center",
            padding: 20,
            background:
              "rgba(15,23,42,0.58)",
          }}
        >
          <section
            className="screenplay-approval-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="screenplay-approval-dialog-title"
            aria-describedby="screenplay-approval-dialog-description screenplay-approval-dialog-note"
            style={{
              width:
                "min(100%, 540px)",
              padding: 24,
              border:
                "1px solid #dbe7df",
              borderRadius: 18,
              background:
                "#ffffff",
              boxShadow:
                "0 24px 70px rgba(15,23,42,0.28)",
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 900,
                letterSpacing:
                  "0.1em",
                textTransform:
                  "uppercase",
                color:
                  "#15803d",
              }}
            >
              {isFR
                ? "BROUILLON APPROUVÉ"
                : "APPROVED DRAFT"}
            </div>

            <h3
              id="screenplay-approval-dialog-title"
              style={{
                margin:
                  "7px 0 8px",
                color:
                  "#0f172a",
              }}
            >
              {isFR
                ? "Approuver ce brouillon ?"
                : "Approve this draft?"}
            </h3>

            <p
              id="screenplay-approval-dialog-description"
              style={{
                margin:
                  "0 0 10px",
                color:
                  "#334155",
                lineHeight: 1.55,
              }}
            >
              {isFR
                ? `La révision ${approvalDialogRevision} sera conservée comme brouillon approuvé. Vous pourrez continuer à écrire ensuite ; les modifications ultérieures ne changeront pas cette version approuvée.`
                : `Revision ${approvalDialogRevision} will be preserved as an Approved Draft. You can keep writing afterward; later edits will not change this approved version.`}
            </p>

            <p
              id="screenplay-approval-dialog-note"
              className="ns-card-meta"
              style={{
                margin: 0,
                lineHeight: 1.5,
                fontWeight: 700,
              }}
            >
              {isFR
                ? "Cela ne publie ni ne diffuse le scénario."
                : "This does not publish or release the screenplay."}
            </p>

            <div
              style={{
                display:
                  "flex",
                flexWrap:
                  "wrap",
                justifyContent:
                  "flex-end",
                gap: 10,
                marginTop: 22,
              }}
            >
              <button
                type="button"
                autoFocus
                className="ns-button ns-button-outline"
                disabled={
                  approving
                }
                onClick={
                  cancelApproveDraft
                }
              >
                {isFR
                  ? "Annuler"
                  : "Cancel"}
              </button>

              <button
                type="button"
                className="ns-button"
                disabled={
                  approving
                }
                onClick={
                  confirmApproveDraft
                }
                style={{
                  background:
                    "#15803d",
                  borderColor:
                    "#15803d",
                  color:
                    "#ffffff",
                }}
              >
                {approving
                  ? (
                      isFR
                        ? "Approbation…"
                        : "Approving…"
                    )
                  : (
                      isFR
                        ? "Approuver le brouillon"
                        : "Approve draft"
                    )}
              </button>
            </div>
          </section>
        </div>
      ) : null}

      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          gap: 16,
          alignItems:
            "flex-start",
          flexWrap: "wrap",
          marginBottom: 18,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              letterSpacing:
                "0.08em",
              textTransform:
                "uppercase",
              color:
                "#dc2626",
              marginBottom: 5,
            }}
          >
            {isFR
              ? "ÉCRITURE DU SCÉNARIO"
              : "SCREENPLAY DRAFT"}
          </div>

          <h2
            className="ns-card-title"
            style={{
              margin:
                "0 0 6px",
            }}
          >
            {isFR
              ? "Script Workspace"
              : "Script Workspace"}
          </h2>

          <p
            className="ns-card-meta"
            style={{
              margin: 0,
              maxWidth: 760,
              lineHeight: 1.5,
            }}
          >
            {isFR
              ? "Écrivez scène par scène. NextScenes conserve la structure du scénario pendant que vous gardez le contrôle du texte."
              : "Write scene by scene. NextScenes keeps the screenplay structure while you stay in control of the words."}
          </p>
        </div>

        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems:
              "center",
            flexWrap: "wrap",
          }}
        >
          <span
            className="ns-card-meta"
            style={{
              display:
                "inline-flex",
              alignItems:
                "center",
              padding:
                "4px 8px",
              border:
                "1px solid #bfdbfe",
              borderRadius:
                999,
              background:
                "#eff6ff",
              color:
                "#1d4ed8",
              fontWeight:
                800,
            }}
          >
            {isFR
              ? `Révision ${revision}`
              : `Revision ${revision}`}
          </span>

          {approvedDraft ? (
            <span
              className="ns-card-meta"
              style={{
                display:
                  "inline-flex",
                alignItems:
                  "center",
                padding:
                  "4px 8px",
                border:
                  "1px solid #bbf7d0",
                borderRadius:
                  999,
                background:
                  "#f0fdf4",
                color:
                  "#166534",
                fontWeight:
                  800,
              }}
            >
              {isFR
                ? `Brouillon approuvé · Révision ${approvedDraft.sourceDraftRevision}`
                : `Approved Draft · Revision ${approvedDraft.sourceDraftRevision}`}
            </span>
          ) : null}

          {releasedDraft ? (
            <span
              className="ns-card-meta"
              style={{
                display:
                  "inline-flex",
                alignItems:
                  "center",
                padding:
                  "4px 8px",
                border:
                  "1px solid #fde68a",
                borderRadius:
                  999,
                background:
                  "#fffbeb",
                color:
                  "#92400e",
                fontWeight:
                  800,
              }}
            >
              {isFR
                ? `Brouillon diffusé · Révision ${releasedDraft.sourceDraftRevision}`
                : `Released Draft · Revision ${releasedDraft.sourceDraftRevision}`}
            </span>
          ) : null}

          {releasedDraft ? (
            <a
              className="ns-button ns-button-outline"
              href={`/reader/screenplay/${encodeURIComponent(
                id
              )}`}
              style={{
                textDecoration:
                  "none",
              }}
            >
              {isFR
                ? "Ouvrir Reader View"
                : "Open Reader View"}
            </a>
          ) : null}

          {(
            !approvedDraft ||
            Number(
              approvedDraft
                .sourceDraftRevision
            ) !==
              Number(revision)
          ) ? (
            <button
              type="button"
              className="ns-button ns-button-outline"
              disabled={
                approving ||
                saving ||
                dirty ||
                revision < 1 ||
                scenes.length === 0
              }
              title={
                dirty
                  ? (
                      isFR
                        ? "Enregistrez vos modifications avant d’approuver ce brouillon."
                        : "Save your changes before approving this draft."
                    )
                  : ""
              }
              onClick={
                requestApproveDraft
              }
            >
              {approving
                ? (
                    isFR
                      ? "Approbation…"
                      : "Approving…"
                  )
                : (
                    isFR
                      ? "Approuver ce brouillon"
                      : "Approve this draft"
                  )}
            </button>
          ) : null}

          {approvedDraft &&
          (
            !releasedDraft ||
            clean(
              releasedDraft
                .approvedDraftId
            ) !==
              clean(
                approvedDraft.id
              )
          ) ? (
            <button
              type="button"
              className="ns-button ns-button-outline"
              disabled={
                releasing ||
                approving
              }
              onClick={
                requestReleaseApprovedDraft
              }
              style={{
                borderColor:
                  "#d97706",
                color:
                  "#92400e",
                background:
                  "#fffbeb",
              }}
            >
              {releasing
                ? (
                    isFR
                      ? "Diffusion…"
                      : "Releasing…"
                  )
                : (
                    isFR
                      ? "Diffuser le brouillon approuvé"
                      : "Release approved draft"
                  )}
            </button>
          ) : null}

        </div>
      </div>

      {message ? (
        <div
          className="ns-card-soft"
          style={{
            padding:
              "10px 12px",
            marginBottom: 16,
          }}
        >
          {message}
        </div>
      ) : null}

      {scenes.length ===
      0 ? (
        <div
          className="ns-card-soft"
          style={{
            padding: 24,
            textAlign:
              "center",
          }}
        >
          <h3
            style={{
              margin:
                "0 0 8px",
            }}
          >
            {isFR
              ? "Commencez votre première scène"
              : "Start your first scene"}
          </h3>

          <p
            className="ns-card-meta"
            style={{
              margin:
                "0 auto 15px",
              maxWidth: 600,
            }}
          >
            {isFR
              ? "Ajoutez une scène. Elle commence par un en-tête de scène, puis vous pouvez ajouter action, personnage, dialogue, parenthèse ou transition."
              : "Add a scene. It starts with a Scene Heading, then you can add Action, Character Cue, Dialogue, Parenthetical or Transition elements."}
          </p>

          <button
            type="button"
            className="ns-button ns-button-primary"
            onClick={
              addScene
            }
          >
            {isFR
              ? "Ajouter la première scène"
              : "Add First Scene"}
          </button>
        </div>
      ) : (
        <>
          <div
            className="ns-screenplay-scene-tools"
          >
            <div
              className="ns-screenplay-scene-count"
            >
              <strong>
                {isFR
                  ? `${scenes.length} scène${scenes.length === 1 ? "" : "s"}`
                  : `${scenes.length} scene${scenes.length === 1 ? "" : "s"}`}
              </strong>

              {activeSceneIndex >= 0 ? (
                <span
                  className="ns-card-meta"
                >
                  {isFR
                    ? `Scène ${activeSceneIndex + 1} sur ${scenes.length}`
                    : `Scene ${activeSceneIndex + 1} of ${scenes.length}`}
                </span>
              ) : null}
            </div>

            <div
              className="ns-screenplay-scene-tool-actions"
            >
              <button
                type="button"
                className="ns-button ns-button-outline"
                aria-expanded={
                  sceneFinderOpen
                }
                aria-controls="screenplay-scene-finder"
                onClick={
                  toggleSceneFinder
                }
              >
                {sceneFinderOpen
                  ? (
                      isFR
                        ? "Fermer la recherche"
                        : "Close finder"
                    )
                  : (
                      isFR
                        ? "Trouver une scène"
                        : "Find scene"
                    )}
              </button>

              <button
                type="button"
                className="ns-button ns-button-outline"
                disabled={dirty}
                onClick={
                  addScene
                }
              >
                {isFR
                  ? "Ajouter une scène"
                  : "Add Scene"}
              </button>
            </div>
          </div>

          {sceneFinderOpen ? (
            <section
              id="screenplay-scene-finder"
              className="ns-screenplay-scene-finder"
              aria-label={
                isFR
                  ? "Trouver une scène"
                  : "Find a Scene"
              }
            >
              <label
                htmlFor="screenplay-scene-finder-input"
              >
                <strong>
                  {isFR
                    ? "Trouver une scène"
                    : "Find a Scene"}
                </strong>

                <span
                  className="ns-card-meta"
                >
                  {isFR
                    ? "Recherchez par numéro de scène, en-tête, personnage, action ou dialogue."
                    : "Search by Scene number, heading, character, action or dialogue."}
                </span>
              </label>

              <input
                id="screenplay-scene-finder-input"
                type="search"
                autoFocus
                className="ns-input"
                value={
                  sceneFinderQuery
                }
                placeholder={
                  isFR
                    ? "Ex. 47, HÔTEL, AMAKA, porte…"
                    : "e.g. 47, HOTEL, AMAKA, door…"
                }
                onChange={(
                  event
                ) =>
                  setSceneFinderQuery(
                    event.target.value
                  )
                }
              />

              {clean(
                sceneFinderQuery
              ) ? (
                sceneFinderResults.length ===
                0 ? (
                  <div
                    className="ns-screenplay-scene-finder-empty"
                  >
                    {isFR
                      ? "Aucune scène correspondante."
                      : "No matching Scene found."}
                  </div>
                ) : (
                  <>
                    <div
                      className="ns-screenplay-scene-finder-summary"
                    >
                      {isFR
                        ? `${sceneFinderResults.length} résultat${sceneFinderResults.length === 1 ? "" : "s"}`
                        : `${sceneFinderResults.length} result${sceneFinderResults.length === 1 ? "" : "s"}`}
                    </div>

                    <div
                      className="ns-screenplay-scene-finder-results"
                    >
                      {visibleSceneFinderResults.map(
                        (result) => {
                          const heading =
                            sceneHeadingText(
                              result.scene
                            );

                          const snippet =
                            sceneFinderSnippet(
                              result.scene
                            );

                          const current =
                            result.sceneIndex ===
                            activeSceneIndex;

                          return (
                            <button
                              key={
                                result.scene
                                  .sceneId
                              }
                              type="button"
                              className={[
                                "ns-screenplay-scene-finder-result",
                                current
                                  ? "is-current"
                                  : "",
                              ].join(" ")}
                              onClick={() =>
                                chooseSceneFinderResult(
                                  result.sceneIndex
                                )
                              }
                            >
                              <span
                                className="ns-screenplay-scene-finder-result-number"
                              >
                                {isFR
                                  ? `Scène ${result.sceneIndex + 1}`
                                  : `Scene ${result.sceneIndex + 1}`}
                              </span>

                              <span
                                className="ns-screenplay-scene-finder-result-copy"
                              >
                                <strong>
                                  {heading ||
                                    (
                                      isFR
                                        ? "Sans en-tête"
                                        : "Untitled Scene"
                                    )}
                                </strong>

                                {snippet ? (
                                  <span>
                                    {snippet}
                                  </span>
                                ) : null}
                              </span>

                              {current ? (
                                <span
                                  className="ns-screenplay-scene-finder-current"
                                >
                                  {isFR
                                    ? "Actuelle"
                                    : "Current"}
                                </span>
                              ) : null}
                            </button>
                          );
                        }
                      )}
                    </div>

                    {sceneFinderResults.length >
                    visibleSceneFinderResults.length ? (
                      <div
                        className="ns-screenplay-scene-finder-summary"
                      >
                        {isFR
                          ? `Les ${visibleSceneFinderResults.length} premiers résultats sont affichés. Affinez la recherche pour réduire la liste.`
                          : `Showing the first ${visibleSceneFinderResults.length} results. Refine the search to narrow the list.`}
                      </div>
                    ) : null}
                  </>
                )
              ) : (
                <div
                  className="ns-screenplay-scene-finder-empty"
                >
                  {isFR
                    ? "Commencez à saisir pour trouver directement une scène, même dans un scénario très long."
                    : "Start typing to jump directly to a Scene, even in a long screenplay."}
                </div>
              )}
            </section>
          ) : null}

          <div
            className="ns-screenplay-scene-navigator"
            aria-label={
              isFR
                ? "Navigation des scènes"
                : "Scene navigation"
            }
          >
            <button
              type="button"
              className="ns-button ns-button-outline ns-screenplay-scene-nav-arrow"
              disabled={
                dirty ||
                activeSceneIndex <= 0
              }
              onClick={() =>
                selectScene(
                  activeSceneIndex - 1
                )
              }
              aria-label={
                isFR
                  ? "Scène précédente"
                  : "Previous Scene"
              }
            >
              ←
            </button>

            <div
              ref={sceneStripRef}
              className="ns-screenplay-scene-strip"
            >
              {scenes.map(
                (
                  scene,
                  sceneIndex
                ) => {
                  const heading =
                    sceneHeadingText(
                      scene
                    );

                  const baselineScene =
                    baselineScenes.find(
                      (row) =>
                        clean(
                          row?.sceneId
                        ) ===
                        clean(
                          scene
                            ?.sceneId
                        )
                    );

                  const saved =
                    Boolean(
                      baselineScene &&
                      signature([
                        baselineScene,
                      ]) ===
                        signature([
                          scene,
                        ])
                    );

                  const active =
                    sceneIndex ===
                    activeSceneIndex;

                  return (
                    <button
                      key={
                        scene.sceneId
                      }
                      type="button"
                      data-screenplay-scene-index={
                        sceneIndex
                      }
                      className={[
                        "ns-screenplay-scene-card",
                        active
                          ? "is-active"
                          : "",
                      ].join(" ")}
                      disabled={
                        dirty &&
                        !active
                      }
                      onClick={() =>
                        selectScene(
                          sceneIndex
                        )
                      }
                    >
                      <span
                        className="ns-screenplay-scene-card-number"
                      >
                        {isFR
                          ? `Scène ${sceneIndex + 1}`
                          : `Scene ${sceneIndex + 1}`}
                      </span>

                      <span
                        className="ns-screenplay-scene-card-heading"
                      >
                        {heading ||
                          (
                            isFR
                              ? "Sans en-tête"
                              : "Untitled Scene"
                          )}
                      </span>

                      <span
                        className="ns-screenplay-scene-card-status"
                      >
                        {active &&
                        isEditingActiveScene
                          ? (
                              dirty
                                ? (
                                    isFR
                                      ? "Modification"
                                      : "Editing"
                                  )
                                : (
                                    isFR
                                      ? "Prête à modifier"
                                      : "Ready to edit"
                                  )
                            )
                          : saved
                            ? (
                                isFR
                                  ? "Enregistrée"
                                  : "Saved"
                              )
                            : (
                                isFR
                                  ? "Non enregistrée"
                                  : "Unsaved"
                              )}
                      </span>
                    </button>
                  );
                }
              )}
            </div>

            <button
              type="button"
              className="ns-button ns-button-outline ns-screenplay-scene-nav-arrow"
              disabled={
                dirty ||
                activeSceneIndex < 0 ||
                activeSceneIndex >=
                  scenes.length - 1
              }
              onClick={() =>
                selectScene(
                  activeSceneIndex + 1
                )
              }
              aria-label={
                isFR
                  ? "Scène suivante"
                  : "Next Scene"
              }
            >
              →
            </button>
          </div>

          {activeScene ? (
            <div
              className="ns-screenplay-draft-paper"
            >
              <section
                key={
                  activeScene
                    .sceneId
                }
                className={[
                  "ns-card-soft",
                  "ns-screenplay-scene",
                  isEditingActiveScene
                    ? "is-editing"
                    : "is-reading",
                ].join(" ")}
                aria-label={
                  isFR
                    ? `Scène ${activeSceneIndex + 1}`
                    : `Scene ${activeSceneIndex + 1}`
                }
              >
                <div
                  className="ns-screenplay-scene-header"
                >
                  <div>
                    <div
                      className="ns-screenplay-scene-number"
                    >
                      {isFR
                        ? `Scène ${activeSceneIndex + 1}`
                        : `Scene ${activeSceneIndex + 1}`}
                    </div>

                    <div
                      className="ns-screenplay-active-heading"
                    >
                      {sceneHeadingText(
                        activeScene
                      ) ||
                        (
                          isFR
                            ? "Scène sans titre"
                            : "Untitled Scene"
                        )}
                    </div>
                  </div>

                  <div
                    className="ns-screenplay-scene-header-actions"
                  >
                    {isEditingActiveScene ? (
                      <>
                        <button
                          type="button"
                          className="ns-button ns-button-primary"
                          disabled={
                            saving ||
                            !dirty
                          }
                          onClick={
                            saveDraft
                          }
                        >
                          {saving
                            ? (
                                isFR
                                  ? "Enregistrement…"
                                  : "Saving…"
                              )
                            : (
                                isFR
                                  ? "Enregistrer la scène"
                                  : "Save Scene"
                              )}
                        </button>

                        <button
                          type="button"
                          className="ns-button ns-button-outline"
                          disabled={
                            activeSceneIndex ===
                            0
                          }
                          onClick={() =>
                            moveScene(
                              activeSceneIndex,
                              -1
                            )
                          }
                        >
                          {isFR
                            ? "Plus tôt"
                            : "Move earlier"}
                        </button>

                        <button
                          type="button"
                          className="ns-button ns-button-outline"
                          disabled={
                            activeSceneIndex ===
                            scenes.length - 1
                          }
                          onClick={() =>
                            moveScene(
                              activeSceneIndex,
                              1
                            )
                          }
                        >
                          {isFR
                            ? "Plus tard"
                            : "Move later"}
                        </button>

                        <button
                          type="button"
                          className="ns-button ns-button-outline"
                          onClick={() =>
                            removeScene(
                              activeSceneIndex
                            )
                          }
                        >
                          {isFR
                            ? "Supprimer la scène"
                            : "Remove Scene"}
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="ns-button ns-button-primary"
                        onClick={
                          beginSceneEdit
                        }
                      >
                        {isFR
                          ? "Modifier la scène"
                          : "Edit Scene"}
                      </button>
                    )}
                  </div>
                </div>

                <div
                  className="ns-screenplay-elements"
                >
                  {activeScene.elements
                    .map(
                      (
                        element,
                        elementIndex
                      ) => (
                        <div
                          key={
                            element
                              .elementId
                          }
                          className={[
                            "ns-screenplay-element-row",
                            screenplayElementClass(
                              element.type
                            ),
                          ].join(" ")}
                        >
                          {isEditingActiveScene ? (
                            <div
                              className="ns-screenplay-element-toolbar"
                            >
                              <div
                                className="ns-screenplay-element-type-control"
                              >
                                <label>
                                  {isFR
                                    ? "Élément"
                                    : "Element"}
                                </label>

                                <select
                                  className="ns-input"
                                  value={
                                    element.type
                                  }
                                  onChange={
                                    (
                                      event
                                    ) =>
                                      updateElement(
                                        activeSceneIndex,
                                        elementIndex,
                                        "type",
                                        event
                                          .target
                                          .value
                                      )
                                  }
                                >
                                  {ELEMENT_TYPES
                                    .map(
                                      (
                                        type
                                      ) => (
                                        <option
                                          key={
                                            type
                                          }
                                          value={
                                            type
                                          }
                                        >
                                          {
                                            TYPE_LABELS[
                                              type
                                            ][
                                              language
                                            ]
                                          }
                                        </option>
                                      )
                                    )}
                                </select>
                              </div>

                              <div
                                className="ns-screenplay-element-actions"
                              >
                                <button
                                  type="button"
                                  className="ns-button ns-button-outline"
                                  disabled={
                                    elementIndex ===
                                    0
                                  }
                                  onClick={() =>
                                    moveElement(
                                      activeSceneIndex,
                                      elementIndex,
                                      -1
                                    )
                                  }
                                >
                                  ↑
                                </button>

                                <button
                                  type="button"
                                  className="ns-button ns-button-outline"
                                  disabled={
                                    elementIndex ===
                                    activeScene
                                      .elements
                                      .length -
                                      1
                                  }
                                  onClick={() =>
                                    moveElement(
                                      activeSceneIndex,
                                      elementIndex,
                                      1
                                    )
                                  }
                                >
                                  ↓
                                </button>

                                <button
                                  type="button"
                                  className="ns-button ns-button-outline"
                                  onClick={() =>
                                    removeElement(
                                      activeSceneIndex,
                                      elementIndex
                                    )
                                  }
                                >
                                  {isFR
                                    ? "Supprimer"
                                    : "Remove"}
                                </button>
                              </div>
                            </div>
                          ) : null}

                          <div
                            className="ns-screenplay-element-copy"
                          >
                            {isEditingActiveScene ? (
                              <>
                                <label>
                                  {
                                    TYPE_LABELS[
                                      element
                                        .type
                                    ][
                                      language
                                    ]
                                  }
                                </label>

                                <textarea
                                  className="ns-input ns-screenplay-element-text"
                                  data-screenplay-element-text="true"
                                  autoComplete="off"
                                  rows={
                                    element.type ===
                                      "dialogue" ||
                                    element.type ===
                                      "action"
                                      ? 3
                                      : 2
                                  }
                                  value={
                                    element.text
                                  }
                                  placeholder={
                                    PLACEHOLDERS[
                                      element
                                        .type
                                    ][
                                      language
                                    ]
                                  }
                                  onChange={
                                    (
                                      event
                                    ) => {
                                      updateElement(
                                        activeSceneIndex,
                                        elementIndex,
                                        "text",
                                        event
                                          .target
                                          .value
                                      );

                                      resizeScreenplayTextarea(
                                        event.currentTarget
                                      );
                                    }
                                  }
                                />
                              </>
                            ) : (
                              <div
                                className="ns-screenplay-element-readonly"
                              >
                                {element.text ||
                                  " "}
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    )}
                </div>

                {isEditingActiveScene ? (
                  <div
                    className="ns-screenplay-add-element-row"
                  >
                    <button
                      type="button"
                      className="ns-button ns-button-outline"
                      onClick={() =>
                        addElement(
                          activeSceneIndex
                        )
                      }
                    >
                      {isFR
                        ? "Ajouter un élément"
                        : "Add Element"}
                    </button>
                  </div>
                ) : null}
              </section>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
